<template>
  <v-layout align-start>
    <v-flex>
      <!-- ── Toolbar ─────────────────────────────────────────────────── -->
      <v-toolbar flat color="transparent">
        <v-toolbar-title>Portada (Hero Slides)</v-toolbar-title>
        <v-divider class="mx-2" inset vertical></v-divider>
        <span class="caption grey--text">Las imágenes se formatean automáticamente a 16:9 (1920×1080)</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark @click="openSlideDialog(null)">
          <v-icon left>add</v-icon>Nuevo slide
        </v-btn>
      </v-toolbar>

      <!-- ── Grilla de slides ────────────────────────────────────────── -->
      <div v-if="!heroSlides.length" class="pa-6 text-center grey--text">
        No hay slides de portada
      </div>
      <v-row v-else class="pa-2">
        <v-col v-for="s in sortedSlides" :key="s._id" cols="12" sm="6" md="4">
          <v-card outlined>
            <div style="position:relative;">
              <v-img
                :src="s.imageUrl"
                height="160"
                :aspect-ratio="16 / 9"
                class="grey darken-3"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-icon color="grey darken-1" large>image</v-icon>
                  </v-row>
                </template>
              </v-img>
              <v-chip
                small
                :color="s.active ? 'green' : 'grey'"
                dark
                style="position:absolute; top:8px; right:8px;"
              >
                {{ s.active ? "Activo" : "Inactivo" }}
              </v-chip>
            </div>

            <v-card-title class="body-1 font-weight-bold" style="word-break:break-word;">
              {{ s.title }}
            </v-card-title>
            <v-card-subtitle v-if="s.subtitle" style="word-break:break-word;">
              {{ s.subtitle.slice(0, 80) }}{{ s.subtitle.length > 80 ? "…" : "" }}
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <v-chip x-small outlined class="mr-1">Orden {{ s.order }}</v-chip>
              <v-chip v-if="s.badge" x-small outlined>{{ s.badge }}</v-chip>
            </v-card-text>

            <v-card-actions>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" @click="openSlideDialog(s)"><v-icon small>edit</v-icon></v-btn>
                </template>
                <span>Editar texto</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" @click="openImageDialog(s)"><v-icon small>image</v-icon></v-btn>
                </template>
                <span>Cambiar imagen</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" @click="toggleActive(s)">
                    <v-icon small>{{ s.active ? "visibility" : "visibility_off" }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ s.active ? "Desactivar" : "Activar" }}</span>
              </v-tooltip>
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small color="red" v-on="on" @click="confirmDelete(s)"><v-icon small>delete</v-icon></v-btn>
                </template>
                <span>Eliminar</span>
              </v-tooltip>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── Dialog crear/editar slide (texto) ──────────────────────── -->
      <v-dialog v-model="slideDialog" max-width="680" persistent>
        <v-card>
          <v-card-title class="headline primary white--text">
            {{ editingSlide ? "Editar Slide" : "Nuevo Slide" }}
          </v-card-title>
          <v-card-text class="pt-4">
            <div v-if="!editingSlide" class="mb-4">
              <div class="caption grey--text mb-1">Imagen de fondo (16:9, se formatea automáticamente)</div>
              <v-btn small outlined @click="$refs.slideImgInput.click()">
                <v-icon left small>upload</v-icon>
                {{ slideImageFile ? "Cambiar imagen" : "Subir imagen" }}
              </v-btn>
              <input ref="slideImgInput" type="file" accept="image/*" style="display:none" @change="onSlideImageChange" />
              <div v-if="slideImageFile" class="caption mt-1">Archivo: {{ slideImageFile.name }}</div>
            </div>

            <v-text-field v-model="slideForm.title" label="Título *" outlined></v-text-field>
            <v-textarea v-model="slideForm.subtitle" label="Subtítulo" outlined rows="2"></v-textarea>
            <v-row>
              <v-col cols="6">
                <v-text-field v-model="slideForm.badge" label="Badge / Pill" outlined></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model.number="slideForm.order" label="Orden" outlined type="number"></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="slideForm.primaryCtaText" label="Botón principal — texto" outlined></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="slideForm.primaryCtaUrl" label="Botón principal — URL" outlined></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="slideForm.secondaryCtaText" label="Link secundario — texto" outlined></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="slideForm.secondaryCtaUrl" label="Link secundario — URL" outlined></v-text-field>
              </v-col>
            </v-row>
            <v-switch v-model="slideForm.active" label="Activo (visible en el carrusel)"></v-switch>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="slideDialog = false">Cancelar</v-btn>
            <v-btn color="primary" :disabled="!slideForm.title" @click="saveSlide">
              {{ editingSlide ? "Guardar" : "Crear" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ── Dialog cambiar imagen ───────────────────────────────────── -->
      <v-dialog v-model="imageDialog" max-width="480" persistent>
        <v-card v-if="imageTarget">
          <v-card-title class="headline primary white--text">
            Cambiar imagen — {{ imageTarget.title }}
          </v-card-title>
          <v-card-text class="pt-4">
            <v-img
              v-if="imageTarget.imageUrl"
              :src="imageTarget.imageUrl"
              :aspect-ratio="16 / 9"
              class="grey darken-3 mb-3"
            ></v-img>
            <div class="caption grey--text mb-2">La imagen se formateará automáticamente a 1920×1080 (16:9)</div>
            <v-btn small outlined @click="$refs.newImgInput.click()">
              <v-icon left small>upload</v-icon>Elegir imagen
            </v-btn>
            <input ref="newImgInput" type="file" accept="image/*" style="display:none" @change="onNewImageChange" />
            <div v-if="newImageFile" class="caption mt-1">Archivo: {{ newImageFile.name }}</div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="imageDialog = false">Cancelar</v-btn>
            <v-btn color="primary" :disabled="!newImageFile" @click="saveImage">Subir imagen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";
import swal from "sweetalert";

export default {
  data() {
    return {
      slideDialog: false,
      editingSlide: null,
      slideImageFile: null,
      slideForm: {
        title: "",
        subtitle: "",
        badge: "",
        primaryCtaText: "",
        primaryCtaUrl: "",
        secondaryCtaText: "",
        secondaryCtaUrl: "",
        order: 0,
        active: true,
      },

      imageDialog: false,
      imageTarget: null,
      newImageFile: null,
    };
  },

  created() {
    this.getHeroSlides();
  },

  computed: {
    ...mapState("heroSlidesNamespace", ["heroSlides"]),

    sortedSlides() {
      return [...this.heroSlides].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    },
  },

  methods: {
    ...mapActions("heroSlidesNamespace", [
      "getHeroSlides",
      "createHeroSlide",
      "updateHeroSlide",
      "toggleHeroSlideActive",
      "updateHeroSlideImage",
      "deleteHeroSlide",
    ]),

    openSlideDialog(slide) {
      if (slide) {
        this.editingSlide = slide;
        this.slideForm = {
          title: slide.title || "",
          subtitle: slide.subtitle || "",
          badge: slide.badge || "",
          primaryCtaText: slide.primaryCtaText || "",
          primaryCtaUrl: slide.primaryCtaUrl || "",
          secondaryCtaText: slide.secondaryCtaText || "",
          secondaryCtaUrl: slide.secondaryCtaUrl || "",
          order: slide.order ?? 0,
          active: slide.active ?? true,
        };
      } else {
        this.editingSlide = null;
        this.slideImageFile = null;
        this.slideForm = {
          title: "",
          subtitle: "",
          badge: "",
          primaryCtaText: "",
          primaryCtaUrl: "",
          secondaryCtaText: "",
          secondaryCtaUrl: "",
          order: 0,
          active: true,
        };
      }
      this.slideDialog = true;
    },

    onSlideImageChange(e) {
      const file = e.target.files[0];
      if (file) this.slideImageFile = file;
    },

    async saveSlide() {
      if (this.editingSlide) {
        await this.updateHeroSlide({ id: this.editingSlide._id, data: this.slideForm });
      } else {
        const fd = new FormData();
        Object.entries(this.slideForm).forEach(([k, v]) => fd.append(k, v));
        if (this.slideImageFile) fd.append("image", this.slideImageFile);
        await this.createHeroSlide(fd);
      }
      this.slideDialog = false;
    },

    toggleActive(slide) {
      this.toggleHeroSlideActive(slide);
    },

    openImageDialog(slide) {
      this.imageTarget = slide;
      this.newImageFile = null;
      this.imageDialog = true;
    },

    onNewImageChange(e) {
      const file = e.target.files[0];
      if (file) this.newImageFile = file;
    },

    async saveImage() {
      if (!this.newImageFile) return;
      const fd = new FormData();
      fd.append("image", this.newImageFile);
      await this.updateHeroSlideImage({ id: this.imageTarget._id, formData: fd });
      this.imageDialog = false;
    },

    confirmDelete(slide) {
      swal({
        title: "¿Eliminar slide?",
        text: `Se eliminará "${slide.title}" del carrusel de portada.`,
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      }).then((ok) => {
        if (ok) this.deleteHeroSlide(slide._id);
      });
    },
  },
};
</script>
